import { ConnectedPosition } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
  TemplateRef,
  ChangeDetectorRef,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorIntl, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule, MatTable } from '@angular/material/table';
import { IonicModule } from '@ionic/angular';
import { Observable, of } from 'rxjs';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';

import { TooltipModule } from '../tooltip/tooltip.module';
import { ActionConfig, ColorTypeEnum, ColumnConfig, PaginationConfig } from './azzurro-table.models';
import { CustomPaginatorIntl, NO_LEN } from './custom-paginator-intl';
import { ApplyPipeModule } from '../apply/apply.module';

const getLabels = () => ({
  search: 'Cerca',
  noData: 'Nessun dato disponibile',
  selectColumns: 'Seleziona colonne',
  noResults: 'Nessun risultato trovato',
  itemsPerPageLabel: 'Elementi per pagina',
  of: 'di',
  actions: 'Azioni',
});

@Component({
  standalone: true,
  imports: [
    ApplyPipeModule,
    CommonModule,
    IonicModule,
    MatButtonModule,
    MatCheckboxModule,
    MatChipsModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatSortModule,
    MatTableModule,
    MatTooltipModule,
    TooltipModule,
  ],
  providers: [
    {
      provide: MatPaginatorIntl,
      useClass: CustomPaginatorIntl,
    },
    CustomPaginatorIntl
  ],
  selector: 'azzurro-table',
  templateUrl: './azzurro-table.component.html',
  styleUrls: ['./azzurro-table.component.scss']
})
export class AzzurroTable<T> implements OnInit, AfterViewInit, OnChanges {
  @Input() tableId!: string;
  @Input() data$: Observable<T[] | null> = of([]);

  private _columns: ColumnConfig[] = [];
  @Input()
  set columns(value: ColumnConfig[]) {
    // Normalize hidden flag e crea un nuovo array per forzare il rendering
    this._columns = value.map(col => ({ ...col, hidden: col.hidden ?? false }));
    this._columns = [...this._columns]; // Nuovo riferimento
    this.updateTableColumns();
  }
  trackByColumn(index: number, column: ColumnConfig): string {
    return column.columnDef;
  }
  get columns(): ColumnConfig[] {
    return this._columns;
  }
  @Input() tableActions?: ActionConfig<T>[];
  @Input() mode: 'client' | 'server' = 'client';
  @Input() pagination: boolean = false;
  @Input() paginationConfig?: PaginationConfig;
  @Input() filterEnabled: boolean = false;
  @Input() filterPredicate?: (row: T, filter: string) => boolean;
  @Input() showColumnToggleButton: boolean = false;
  @Input() noResultTemplate?: TemplateRef<any>;
  @Input() uiFramework: 'material' | 'ionic' = 'material';
  @Input() tableLayout: 'auto' | 'fixed' = 'auto';
  @Input() rowClickable?: (row: T) => void;
  @Input() expandAllByDefault: boolean = false;
  @Input() allowMultipleExpansion: boolean = false;
  @Input() rowExpansionKey?: (row: T | null | undefined) => string | number;
  @Input() rowExpansionTemplate?: TemplateRef<any>;
  @Input() rowExpansionTemplateByRow?: (row: T) => TemplateRef<any> | null | undefined;
  @Input() showRowExpansionIcon: boolean = true;
  @Input() allowRowClickExpansion: boolean = true;

  readonly _labels = getLabels();
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
    (this.paginatorIntl as CustomPaginatorIntl).updateLabels({
      itemsPerPageLabel: this.labels.itemsPerPageLabel,
      getRangeLabel: (page, pageSize, length) => {
        if (pageSize === 0) {
          return `0`;
        }

        const startIndex = page * pageSize;
        let endIndex: number;
        if (length === undefined || length === 0) {
          endIndex = (page + 1) * pageSize;
        } else {
          endIndex = startIndex < length
            ? Math.min(startIndex + pageSize, length)
            : startIndex + pageSize;
        }

        if (length === undefined || length === 0 || length === NO_LEN) {
          return `${startIndex + 1} - ${endIndex}`;
        }

        return `${startIndex + 1} - ${endIndex} ${this.labels.of} ${length}`;
      },
    });
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  @Output() sortChanged = new EventEmitter<Sort>();
  @Output() pageChanged = new EventEmitter<PageEvent>();
  @Output() filterChanged = new EventEmitter<string>();

  dataSource: MatTableDataSource<T> = new MatTableDataSource<T>([]);

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatTable) table!: MatTable<T>;

  readonly tooltipsTemplatePosition: Partial<ConnectedPosition> = {
    originX: 'end',
    originY: 'bottom',
    overlayX: 'end',
    overlayY: 'top',
    offsetY: 4,
  };

  get hasAnyStickyConfig(): boolean {
    return this._columns.some(col => col.sticky != null);
  }

  get visibleColumns(): ColumnConfig[] {
    return this.columns.filter(col => !col.hidden).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  get visibleColumnNames(): string[] {
    const cols = this.visibleColumns.map(col => col.columnDef);
    if (this.tableActions) {
      cols.push('actions');
    }
    return cols;
  }

  loading: boolean = true;
  placeholderArray: any[] = new Array(10);

  ColorTypeEnum = ColorTypeEnum;
  readonly toggleRowExpansionFn = (row: T) => this.toggleRowExpansion(row);
  readonly isRowExpandedFn = (row: T) => this.isRowExpanded(row);

  expandedRowKeys = new Set<string | number | T>();
  private hasInitializedExpansion: boolean = false;
  private latestData: T[] = [];

  constructor(private paginatorIntl: MatPaginatorIntl, private cdr: ChangeDetectorRef) {
    this.updateTableColumns = this.updateTableColumns.bind(this);
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.columns) {
      this.updateTableColumns();
    }
    if (changes.filterPredicate) {
      this.updateFilterPredicate();
    }
  }

  ngOnInit(): void {
    this.columns = this.columns.map(col => ({
      ...col,
      hidden: col.hidden ?? false
    }));

    if (this.showColumnToggleButton) {
      this.loadConfiguration();
    }

    this.loading = true;
    this.dataSource.data = this.placeholderArray as T[];
    this.updateFilterPredicate();
    this.data$.subscribe(data => {
      if (data === null) {
        this.loading = true;
        this.dataSource.data = this.placeholderArray as T[];
      } else {
        this.loading = false;
        this.dataSource.data = data;
        this.latestData = data;
        this.initializeExpandedRows(data);

        if (this.pagination && this.paginator && this.paginationConfig?.length !== undefined) {
          this.paginator.length = this.paginationConfig.length;
        }
      }

      setTimeout(() => {
        if (this.mode === 'client') {
          if (this.sort) {
            this.dataSource.sort = this.sort;
          }
          if (this.pagination && this.paginator) {
            this.dataSource.paginator = this.paginator;
          }
        }
      }, 100);
    });
  }

  private updateFilterPredicate(): void {
    if (this.filterPredicate) {
      this.dataSource.filterPredicate = (data: T, filter: string) =>
        this.filterPredicate?.(data, filter) ?? true;
    }
  }

  private initializeExpandedRows(data: T[]): void {
    if (this.hasInitializedExpansion || !this.expandAllByDefault || data.length === 0) {
      return;
    }
    const expandableRows = data.filter(row => this.getRowExpansionTemplate(row));
    if (expandableRows.length === 0) {
      return;
    }
    this.expandedRowKeys = new Set(expandableRows.map(row => this.getRowKey(row)));
    this.hasInitializedExpansion = true;
    this.requestRowRender();
    this.cdr.markForCheck();
  }

  private requestRowRender(): void {
    if (this.table) {
      this.table.renderRows();
      return;
    }
    setTimeout(() => this.table?.renderRows());
  }

  ngAfterViewInit(): void {
    if (this.mode === 'client' && this.sort && this.paginator) {
      this.dataSource.sort = this.sort;
      this.dataSource.paginator = this.paginator;
    }
    if (!this.hasInitializedExpansion && this.expandAllByDefault && this.latestData.length > 0) {
      this.initializeExpandedRows(this.latestData);
    }
  }

  onSortChange(sort: Sort) {
    if (this.mode === 'server') {
      this.sortChanged.emit(sort);
    }
  }

  onPageChange(pageEvent: PageEvent) {
    if (this.mode === 'server') {
      const pageIndex = pageEvent.pageIndex ?? 0;
      const previousPageIndex = pageEvent.previousPageIndex ?? 0;
      const hasMore = this.paginationConfig?.hasMore ?? true;

      if (this.paginationConfig?.length !== undefined && pageIndex > previousPageIndex && !hasMore) {
        this.paginator.pageIndex = previousPageIndex;
        return;
      }

      this.pageChanged.emit(pageEvent);
    }
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    if (this.mode === 'client') {
      this.dataSource.filter = filterValue.trim().toLowerCase();
    } else if (this.mode === 'server') {
      this.filterChanged.emit(filterValue);
    }
  }

  toggleColumnVisibility(column: string): void {
    const colConfig = this.columns.find(col => col.columnDef === column);
    if (colConfig) {
      colConfig.hidden = !colConfig.hidden;
      this.saveConfiguration();
    }
  }

  saveConfiguration(): void {
    const config = this.columns.reduce((acc, col) => {
      acc[col.columnDef] = col.hidden ?? false;
      return acc;
    }, {} as Record<string, boolean>);
    localStorage.setItem(`table-config-${this.tableId}`, JSON.stringify(config));
  }

  loadConfiguration(): void {
    const stored = localStorage.getItem(`table-config-${this.tableId}`);
    if (stored) {
      const config: Record<string, boolean> = JSON.parse(stored);
      this.columns = this.columns.map(col => ({
        ...col,
        hidden: config[col.columnDef] !== undefined ? config[col.columnDef] : false
      }));
    }
  }

  updateTableColumns() {
    if (this.dataSource) {
      if (this.sort) {
        this.dataSource.sort = this.sort;
      }
      if (this.paginator && this.mode === 'client') {
        this.dataSource.paginator = this.paginator;
      }
    }
    // Trigger table rows re-render
    if (this.table) {
      this.table.renderRows();
    }
    // Ensure Angular detects the changes
    this.cdr.detectChanges();
  }

  getRowExpansionTemplate(row: T): TemplateRef<any> | null {
    return (this.rowExpansionTemplateByRow?.(row) ?? this.rowExpansionTemplate) ?? null;
  }

  private getRowKey(row: T): string | number | T {
    return this.rowExpansionKey ? this.rowExpansionKey(row) : row;
  }

  isRowExpanded(row: T): boolean {
    if (row == null) {
      return false;
    }
    return this.expandedRowKeys.has(this.getRowKey(row));
  }

  private canExpandMultiple(): boolean {
    return this.allowMultipleExpansion || this.expandAllByDefault;
  }

  toggleRowExpansion(row: T): void {
    if (row == null) {
      return;
    }
    // Expand only if the row has an expansion template
    if (!this.getRowExpansionTemplate(row)) {
      return;
    }
    if (this.canExpandMultiple()) {
      const key = this.getRowKey(row);
      if (this.expandedRowKeys.has(key)) {
        this.expandedRowKeys.delete(key);
      } else {
        this.expandedRowKeys.add(key);
      }
      this.requestRowRender();
      return;
    }
    const key = this.getRowKey(row);
    if (this.expandedRowKeys.has(key)) {
      this.expandedRowKeys.clear();
      this.requestRowRender();
      return;
    }
    this.expandedRowKeys.clear();
    this.expandedRowKeys.add(key);
    this.requestRowRender();
  }

  isExpansionDetailRow = (_index: number, row: T): boolean => this.isRowExpanded(row);

}
